import Events from '../../lib/events';
import { log } from '../../lib/util';
/* global ga */

const allowedEvents = [
  Events.ERROR,
  Events.PAUSE,
  Events.PLAY,
  Events.VIEW,
  Events.AD_START,
  Events.AD_COMPLETE,
  Events.LOAD_TIME,
  Events.TTFF,
  Events.START,
  Events.THREE_SECONDS,
  Events.TEN_SECONDS,
  Events.THIRTY_SECONDS,
  Events.FIRST_QUARTILE,
  Events.SECOND_QUARTILE,
  Events.THIRD_QUARTILE,
  Events.FOURTH_QUARTILE,
  Events.COMPLETE
];

const prefixedEvents = [
  Events.START,
  Events.ELAPSED,
  Events.ELAPSED_THRESHOLD,
  Events.THREE_SECONDS,
  Events.TEN_SECONDS,
  Events.THIRTY_SECONDS,
  Events.FIRST_QUARTILE,
  Events.SECOND_QUARTILE,
  Events.THIRD_QUARTILE,
  Events.FOURTH_QUARTILE,
  Events.COMPLETE
];

export default class GoogleAnalyticsTracker {
  constructor(player) {
    if (!player.allowPersonalizedCookies()) {
      return;
    }
    this.player = player;
  }

  /**
  * Reporting for analytics event tracking.
  * Will attempt to report to an internally-configured Google Analytics instance, when available.
  * Otherwise, analytics reporting is posted to the parent frame
  * where the embed API may be installed to proxy reporting.
  * @param {Object} containing the following keys
    * action {String} required. Action descriptor for the event.
    * value {Integer} optional. Value for event. This is converted to an integer.
    * message {String} optional. Additional message for event.
    * index {Integer} optional. Index value of item in a playlist.
  */
  track(analyticsEvent) {
    let action = analyticsEvent.action;
    let player = this.player;
    let opts = player.options;
    let video = player.video;


    if (allowedEvents.indexOf(action) > -1) {
      // Ping the exterior host before sending any analytics.
      // This will either establish a connection with the host Chorus page,
      // or else fail and resort to installing Google Analytics locally.
      this.player.host.ping(host => {
        var self = this;
        if (prefixedEvents.indexOf(action) > -1) {
          if (analyticsEvent.index > 0) {
            action = 'discovery' + action;
          } else {
            action = 'content' + action;
          }
        }

        var eventData = {
          eventCategory: analyticsEvent.category || 'video',
          eventLabel: [video.title_short, video.id].join(' | '),
          eventValue: Number(analyticsEvent.value) >= 0 ? Number(analyticsEvent.value) : undefined,
          eventAction: (analyticsEvent.category === 'social') ? action : [player.type, action].join(':'),
          nonInteraction: (action !== Events.PLAY)
        };

        if (analyticsEvent.message) {
          eventData.eventLabel = analyticsEvent.message;
          log('Track Message', analyticsEvent.message);
        }

        /* if an eventValue is undefined, we assume that this is a timing measurement and so we leave out
        the location information.   Otherwise, add the additional location information to the action.
        */
        if (eventData.eventValue === undefined) {
          if (analyticsEvent.index > 0) {
            eventData.eventAction = [player.type, action, 'discovery', 'index' + analyticsEvent.index].join(':');
          } else if (action === 'play' || action === 'view') {
            eventData.eventAction = [player.type, action, opts.tracking || opts.placement || 'other'].join(':');
          }
        }

        // Check if the host provides a reporting API (external Chorus page):
        // if so, analytics get reported via the parent window.
        if (host.api) {
          self.formatDimensions(video).forEach(cd => eventData[cd.label] = cd.data);
          player.host.post('analytics', eventData);

        // If host is not available, then attempt to report using a local GA instance.
        } else if (typeof ga === 'function') {
          self.formatDimensions(video).forEach(cd => ga('set', `dimension${cd.cdIndex}`, cd.data));
          ga('send', 'event', eventData);
          log('GA report', eventData);
        }
      });
    }
  }

  formatDimensions(video) {
    var metadata = video.formatted_metadata;
    return metadata ? [
      { cdIndex: 41, data: metadata.video_id, label: 'Video ID' },
      { cdIndex: 42, data: 'video', label: 'Video Content Type' },
      { cdIndex: 43, data: metadata.credits, label: 'Producer' },
      { cdIndex: 44, data: metadata.video_publish_date, label: 'Video Publish Date' },
      { cdIndex: 45, data: metadata.tags, label: 'Tags' },
      { cdIndex: 46, data: metadata.primary_brand, label: 'Primary Community' },
      { cdIndex: 47, data: metadata.series, label: 'Series' },
      { cdIndex: 48, data: metadata.format, label: 'Format' },
      { cdIndex: 49, data: metadata.subformat, label: 'Sub-format' }
    ].filter(({ data }) => Boolean(data)) : [];
  }
}
